import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

/**
 * First-visit cookie consent banner. Cinematic, dismissible, remembers the
 * user's choice in localStorage. GDPR/CCPA-friendly with equal-weight
 * Accept / Reject buttons + link to the Cookie Policy.
 *
 * Reads `window.__cookieConsent` = "accepted" | "rejected" for any
 * analytics/marketing scripts you gate later.
 */
const STORAGE_KEY = "sb_cookie_consent";

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay to let the hero settle in before the banner slides up.
    const t = setTimeout(() => {
      const choice = localStorage.getItem(STORAGE_KEY);
      if (!choice) setVisible(true);
      else {
        window.__cookieConsent = choice;
        // Sync gtag consent for any dependent script (GA4 already reads
        // localStorage on load, but this covers race conditions where the
        // consent script loaded after the banner mounted).
        try {
          window.dispatchEvent(new CustomEvent("sb-consent-changed", { detail: choice }));
        } catch (_e) { /* CustomEvent unsupported — noop */ }
      }
    }, 1400);
    return () => clearTimeout(t);
  }, []);

  const record = (choice) => {
    localStorage.setItem(STORAGE_KEY, choice);
    localStorage.setItem(`${STORAGE_KEY}_at`, new Date().toISOString());
    window.__cookieConsent = choice;
    try {
      // Notify any consent-gated scripts (GA4, marketing pixels, etc).
      window.dispatchEvent(new CustomEvent("sb-consent-changed", { detail: choice }));
    } catch (_e) { /* noop */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-consent-banner"
          initial={{ x: "-50%", y: 120, opacity: 0 }}
          animate={{ x: "-50%", y: 0, opacity: 1 }}
          exit={{ x: "-50%", y: 120, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 md:bottom-6 left-1/2 z-[70] w-[calc(100%-1.5rem)] max-w-[720px]"
        >
          <div className="relative rounded-2xl border border-white/10 bg-[#0a0a12]/95 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden">
            {/* Ambient glow strip */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00f0ff]/50 to-transparent" />

            <div className="p-5 md:p-6 grid md:grid-cols-[auto_1fr_auto] items-start md:items-center gap-4 md:gap-5">
              <div className="w-10 h-10 rounded-xl border border-white/10 bg-white/[0.04] flex items-center justify-center shrink-0">
                <Cookie size={16} className="text-[#00f0ff]" />
              </div>

              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#00f0ff] mb-1">
                  Cookie notice
                </div>
                <p className="text-sm text-white/80 leading-relaxed">
                  We use cookies to keep the site secure, remember your
                  preferences, and understand how it&rsquo;s used. See our{" "}
                  <Link
                    to="/legal/cookie-policy"
                    data-testid="cookie-consent-policy-link"
                    className="text-[#00f0ff] hover:text-white underline underline-offset-2 whitespace-nowrap"
                  >
                    Cookie Policy
                  </Link>
                  .
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0 md:justify-end">
                <button
                  onClick={() => record("rejected")}
                  data-testid="cookie-consent-reject"
                  className="px-4 h-10 rounded-full border border-white/15 bg-transparent text-xs uppercase tracking-[0.22em] text-white/70 hover:text-white hover:border-white/30 transition-colors"
                >
                  Reject
                </button>
                <button
                  onClick={() => record("accepted")}
                  data-testid="cookie-consent-accept"
                  className="px-4 h-10 rounded-full bg-[#00f0ff] text-[#05050a] text-xs uppercase tracking-[0.22em] font-semibold hover:bg-white transition-colors"
                >
                  Accept
                </button>
                <button
                  onClick={() => record("rejected")}
                  data-testid="cookie-consent-close"
                  aria-label="Dismiss"
                  className="hidden md:inline-flex w-8 h-8 rounded-full items-center justify-center text-white/40 hover:text-white hover:bg-white/5 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
